import { useLocation, useNavigate } from "react-router-dom";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import GenericFormHeader from "../../components/dms_common/GenericFormHeader";
import "./viewSaleReturnDetails.css"
import { useCallback, useEffect, useState } from "react";
import { BatchDetailsMap, Invoice, SalesBatchId, SalesDetail, SalesReasonDetails, TaxDetails } from "../../types/types";
import { getDataFromLocalDb } from "../../services/dbService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { approveSalesReturnWithInvoice, calculateReturnAmount, getBatchDetailsFromDb, getBatchIdsFromMap, getSalesReturnReqBody, rejectSalesReturnWithInvoice } from "../SalesReturn/SalesReturnService";
import Header from "../../components/dms_common/Header";
import CustomAccordian from "../../components/dms_common/CustomAccordian";
import { salesReturnStatusMapping } from "../SalesReturn/SalesReturnUtils";
import { CreditNote } from "../../components/creditNote/creditNote";
import { getCreditAmount } from "../deliveryConfirmation/DeliveryConfirmationService";
import { Loader } from "../../components/loader/Loader";
import { logLoadTrace } from "../../firebase";
import { ChannelkartNetworkPut } from "../../services/networkService";
import SaleReturnRejectReasonModal from "./saleReturnRejectReasonModal";
import SaleReturnApprovalModal from "./saleReturnApprovalModal";
import MultiLevelTable from "../../components/multiLevelTable/multiLevelTable";
import { Box } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { getMergedCatalogueAndRealTimeStockData } from "../stocks/RealTimeStock/RealTimeStockUtils";
import { Product } from "../createSecondaryOrder/models";
import { truncateToTwoDecimals } from "../../components/dms_common/DmsUtils";


const ViewSaleReturnDetails = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const [salesDetails, setSalesDetails] = useState<SalesDetail[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [subTableRowIds, setSubTableRowIds] = useState<string[]>([]);
    const [openCreditNote, setOpenCreditNote] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [totalCreditAmount, setTotalCreditAmount] = useState<number>(0);
    const invoiceData: Invoice = location.state.invoiceDetails;
    const [status, setStatus] = useState<string>(invoiceData.status);
    const [modalType, setModalType] = useState<string>("");
    const initialBatchDetails: BatchDetailsMap = location.state.initialBatchDetails;
    const returnAmountMap:any = location.state.returnAmountMap;
    const headerLabel = location.state.headerLabel;
    if (!invoiceData) {
        navigate(-1);
    }

    const fetchAndUpdateSalesDetails = async () => {
        try {
            const batchDetails = await getBatchDetailsFromDb();
            const salesDetailsMap:any = {};
            batchDetails && invoiceData.salesDetails.forEach(salesDetails => {
                let caseQty = 0, pieceQty = 0, otherQty = 0;
                batchDetails[salesDetails.batchCode] && Object.keys(batchDetails[salesDetails.batchCode]).forEach(batchId => {
                    batchDetails[salesDetails.batchCode][batchId].forEach(batch => {
                        caseQty += batch.caseQty;
                        pieceQty += batch.pieceQty;
                        otherQty += batch.otherQty;
                    })
                })
                salesDetails.returnCaseQty = caseQty;
                salesDetails.returnPieceQty = pieceQty;
                salesDetails.returnOtherQty = otherQty;
                salesDetails.batchIds = batchDetails && batchDetails[salesDetails.batchCode] ? getBatchIdsFromMap(initialBatchDetails[salesDetails.batchCode]) : [];
                salesDetails.batchIds?.forEach(batchId => {
                    batchId.reasonDetails = batchDetails[salesDetails.batchCode][batchId.batchId];
                })
                salesDetails.batchIdDetails = salesDetails.batchIds?.map((batchId: SalesBatchId) => {
                    return batchId.reasonDetails?.map((reasonDetail: SalesReasonDetails) => {
                      return {
                        batchId: batchId.batchId,
                        caseQty: reasonDetail.caseQty,
                        otherQty: reasonDetail.otherQty,
                        pieceQty: reasonDetail.pieceQty,
                        reason: reasonDetail.reason,
                        mrp: salesDetails.mrp
                      }
                    }).flat() ?? [];
                  }).flat() ?? [];
                  if(isPending()){
                    salesDetails.netAmount = calculateReturnAmount(salesDetails);
                  }else{
                    salesDetails.netAmount = parseFloat(truncateToTwoDecimals(returnAmountMap[salesDetails.batchCode] ?? 0));
                  }
                  if(!salesDetailsMap[salesDetails.batchCode]){  
                    salesDetailsMap[salesDetails.batchCode] = salesDetails;
                  }
            })
            invoiceData.salesDetails = Object.values(salesDetailsMap);
            setSalesDetails(invoiceData.salesDetails);
            if (isApproved()) {
                await fetchAndSetCreditAmount();
            } else {
                invoiceData.returnAmount = invoiceData.salesDetails.reduce((prev, curr) => prev + curr.netAmount, 0);
            }
        } catch (e) { }
        setIsSubmitting(false);
    }

    useEffect(() => {
        logLoadTrace(fetchAndUpdateSalesDetails, "view_sale_returns_page_load_time");
    }, [])

    const headerDetails = [
        {
            label: headerLabel || "Sale Returns",
            redirectFunction: () => { navigate(-1) }
        },
        {
            label: "Review"
        }
    ]

    const handleRedirect = async (rowData: SalesDetail) => {
        const productDetails = (await getDataFromLocalDb("Product")).data[0].data;
        if (!productDetails || !productDetails[rowData.batchCode]) {
            openPopup("Error", "Product details not found");
            return;
        }
        const batchDetails = await getBatchDetailsFromDb();
        navigate("edit", {
            state: {
                rowData: rowData,
                productDetails: productDetails[rowData.batchCode],
                showReturnQty: true,
                batchDetails: (batchDetails && batchDetails[rowData.batchCode]) ?? {}
            }
        });
    };

    const handleSearch = useCallback((searchStr: string) => {
        const filteredData = invoiceData.salesDetails.filter(
            (salesDetail: SalesDetail) =>
                salesDetail.batchCode.toLowerCase().includes(searchStr.toLowerCase()) ||
                salesDetail.skuName.toLowerCase().includes(searchStr.toLowerCase()) ||
                salesDetail.packType.toLowerCase().includes(searchStr.toLowerCase())
        );
        setSalesDetails(filteredData);
    }, [])

    const isPending = () => {
        return invoiceData.status.toLowerCase() == "pending";
    }

    const isApproved = () => {
        return invoiceData.status.toLowerCase() == "approved" || invoiceData.status.toLowerCase() == "credit note issued";
    }


    const isReturnEditted = () => {
        let isEditted = false;
        invoiceData.salesDetails.forEach(salesDetail => {
            salesDetail.batchIds?.forEach(batchIdObject => {
                const initialData = initialBatchDetails[salesDetail.batchCode][batchIdObject.batchId];
                if (initialData.length != batchIdObject.reasonDetails?.length) {
                    isEditted = true;
                    return;
                }
                batchIdObject.reasonDetails?.forEach(reasonDetail => {
                    const intialReasonObject = initialData.find(initialBatch => initialBatch.reason == reasonDetail.reason);
                    if (!intialReasonObject || reasonDetail.caseQty != intialReasonObject.caseQty || reasonDetail.pieceQty != intialReasonObject.pieceQty || reasonDetail.otherQty != intialReasonObject.otherQty) {
                        isEditted = true;
                    }
                })
            })
        })
        return isEditted;
    }

    const fetchAndSetCreditAmount = async () => {
        const creditAmount = await getCreditAmount(invoiceData.returnNumber);
        setTotalCreditAmount(creditAmount);
    }
    const handleApproveReturn = async () => {
        const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
        const reasonMap = ConfigDictionary?.salesReturnReasonsMap ?? {};
        setIsSubmitting(true);
        try {
            const requestBody = getSalesReturnReqBody(invoiceData, invoiceData.returnNumber);
            if (requestBody.salesDetails.length == 0) {
                openPopup("Error", "Cannot approve return with 0 quantity");
                setIsSubmitting(false);
                return;
            }
            let response
            if (isReturnEditted() || Object.keys(reasonMap).length == 0) {
                response = await ChannelkartNetworkPut("/v1/sales/editReturn", requestBody);
            } else {
                response = await approveSalesReturnWithInvoice(invoiceData);
            }
            if (response.status == 200) {
                // openPopup("Success", "Return Quantity has been approved successfully");
                invoiceData.status = "APPROVED";
                setStatus(status);
                setModalType("approve");
                window.history.state.usr.invoiceDetails = invoiceData;
                window.history.replaceState(window.history.state, document.title, window.location.pathname);
                fetchAndSetCreditAmount();
            }
        } catch (e) {
            openPopup("Error", "Unable to approve sale return, please try again");
        }
        setIsSubmitting(false);
    }
    const handleRejectReturn = async (reason: string) => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await rejectSalesReturnWithInvoice(invoiceData, reason);
            if (response.status == 200) {
                navigate(-1);
            }
        } catch (e) {
            openPopup("Error", "Unable to reject sale return, please try again");
        }
        setIsSubmitting(false);
    }

    const getFooterElement = () => {
        return (
            <div className="view-sale-return-submit-button-container">
                {!isSubmitting && invoiceData.saleCondition?.toLowerCase() != "unsaleable" && isApproved() && invoiceData.returnAmount - totalCreditAmount > 0 && invoiceData.status.toLowerCase() != "credit note issued" && (
                    <button
                        className={`view-sale-return-submit ${isSubmitting ? "disabled" : ""}`}
                        onClick={() => { setOpenCreditNote(true) }}
                    >
                        <span className="view-sale-return-submit-text">
                            Issue Credit Note
                        </span>
                    </button>
                )}
                {!isSubmitting && invoiceData.status.toLowerCase() == "pending" && (
                    <>
                        <button
                            className={`view-sale-return-submit`}
                            onClick={handleApproveReturn}
                        >
                            <span className="view-sale-return-submit-text">
                                Approve
                            </span>
                        </button>
                        <button
                            className={`view-sale-return-submit reject`}
                            onClick={() => { setModalType("reject") }}
                        >
                            <span className="view-sale-return-submit-text">
                                Reject
                            </span>
                        </button>
                    </>
                )}
                {isSubmitting && (
                    <div style={{ width: "40px", marginRight: "40px" }}>
                        <Loader />
                    </div>
                )}
            </div>
        )
    }

    const getOutletCordianData = () => {
        return {
            outletCode: invoiceData.outletCode,
            outletName: invoiceData.outletName,
            accountType: invoiceData.accountType,
            loadoutNumber: invoiceData.loadoutNumber,
            deliveryMode: invoiceData.deliveryMode,
            vehicleNumber: invoiceData.vehicleNumber
        }
    }

    const getInvoiceCordianData = () => {
        return {
            invoiceNumber: invoiceData.invoiceNumber,
            orderNumber: invoiceData.orderNumber,
            returnDate: invoiceData.returnDate,
            totalProducts: invoiceData.totalProducts,
            invoiceValue: truncateToTwoDecimals(invoiceData.netAmount),
            returnValue: truncateToTwoDecimals(invoiceData.returnAmount)
        };
    }

    useEffect(()=>{
        const dataFunction = async ()=>{
            const productsData = await getDataFromLocalDb("Product");
                    const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
                const mergedData = getMergedCatalogueAndRealTimeStockData(
                    invoiceData.salesDetails,
                    productsMap
                );
            invoiceData.salesDetails= mergedData
        }
        
        dataFunction()
    },[])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (document.querySelector('[role="dialog"]')) return;

            if (modalType == "reject" || modalType == "approve") return;

            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
                event.preventDefault(); // Prevent browser's default select all

                if (isSubmitting) {
                    return;
                }

                if (isApproved() && invoiceData.returnAmount - totalCreditAmount > 0) {
                    logLoadTrace(() => setOpenCreditNote(true), "view_sale_return_issue_credit_note_time");
                } else if (invoiceData.status.toLowerCase() === "pending") {
                    logLoadTrace(handleApproveReturn, "view_sale_return_approve_time");
                }
            }

            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "x") {
                event.preventDefault(); // Prevent browser's default cut

                if (isSubmitting) {
                    return;
                }

                if (invoiceData.status.toLowerCase() === "pending") {
                    logLoadTrace(() => setModalType("reject"), "view_sale_return_reject_time");
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isSubmitting, invoiceData, totalCreditAmount, handleApproveReturn]);

    const handleOpenSubTable = (rowId: string) => {
        if (subTableRowIds.includes(rowId)) {
            setSubTableRowIds(subTableRowIds.filter(id => id != rowId));
        } else {
            setSubTableRowIds([...subTableRowIds, rowId]);
        }
    }
    const isSubTableOpen = (rowId: string) => {
        return subTableRowIds.includes(rowId);
    }

    return (
        <div className="view-sale-return-details-container">
            <GenericFormHeader
                labelDetails={headerDetails}
                searchFunction={handleSearch}
            />
            <CustomAccordian
                expanded={expanded}
                onCustomAccordionToggle={() => { setExpanded(!expanded) }}
                header={`Return Order No. ${invoiceData.returnNumber}`}
                status={status}
                statusConfig={salesReturnStatusMapping}
                metaData1={outletCordianMetaData}
                data1={getOutletCordianData()}
                metaData2={invoiceCordianMetaData}
                data2={getInvoiceCordianData()}
                label1="Outlet Info"
                label2="Invoice Info"
                isSecondHalf={true}
                singleColumnInEachHalf={false}
            />
            <Header
                title={isPending() ? "Review Return Qty" : "Approved Return Qty"}
            />
                <MultiLevelTable
                    rowData={salesDetails}
                    columnsMetaData={getViewSalesReturnDetailsColumnMetaData(handleOpenSubTable, isSubTableOpen, isPending(), handleRedirect)}
                    footerElement={getFooterElement}
                    subTableRowIds={subTableRowIds}
                    subTableMetaData={subTableMetaData}
                /> 
                {/* <CustomDataGrid
                    rows={salesDetails}
                    columnsMetaData={getSalesReturnDetailsColumnMetaData()}
                    tableWidth={0.78}
                    redirectUrl={isPending() ? "edit" : undefined}
                    redirectUrlFunction={isPending() ? handleRedirect : undefined}
                    footerElement={getFooterElement}
                /> */}

            <CreditNote open={openCreditNote} setOpen={setOpenCreditNote} outletCode={invoiceData.outletCode} returnId={invoiceData.returnNumber} returnAmount={(invoiceData.returnAmount) ?? 0} creditAmount={totalCreditAmount} />
            <SaleReturnRejectReasonModal open={modalType == "reject"} closeModal={() => { setModalType("") }} onClick={handleRejectReturn} />
            <SaleReturnApprovalModal open={modalType == "approve"} closeModal={() => { setModalType("") }} onClick={() => { setModalType(""); setOpenCreditNote(true) }} />
        </div>
    )
}

const getViewSalesReturnDetailsColumnMetaData = (handleOpenSubTable: (rowId: string) => void, isSubTableOpen: (rowId: string) => boolean, isPending: boolean, handleRedirect: (rowData: SalesDetail) => void) => [
    {
        headerName: "SKU Code",
        mappedValue: "batchCode",
        width: "13%",
    },
    {
        headerName: "SKU Name",
        mappedValue: "skuName",
        width: "18%",
    },
    {
        headerName: "Pack Type",
        mappedValue: "packType",
        width: "10%",
    },
    {
        headerName: "Batch Ids",
        mappedValue: "batchIdDetails",
        width: "14%",
        renderCell: (row: SalesDetail) => {
            return <Box
            className="batch-drowdown-container"
            onClick={() => handleOpenSubTable(row.id)}
            sx={{
              cursor: "pointer",
              marginLeft: "auto",
              marginRight: "auto",
              backgroundColor: "#F7F7F7",
            }}
          >
            <Box sx={{ color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090", fontSize: "11px" }}>
              View all batch IDs
            </Box>
            <div
              className="icon-border-circle"
              style={{ borderColor: isSubTableOpen(row.id) ? "#17B4A3" : "#909090" }}
            >
              {isSubTableOpen(row.id) ? (
                <KeyboardArrowUp
                  style={{
                    fontSize: "13px",
                    color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090",
                  }}
                />
              ) : (
                <KeyboardArrowDown style={{ fontSize: "13px" }} />
              )}
            </div>
          </Box>
        }
    },
    {
        headerName: "PTR",
        mappedValue: "price",
        width: "8%",
        displayType: "amount"
    },
    {
        headerName: "Sold Qty",
        mappedValue: "soldCaseQty,soldPieceQty,soldOtherQty",
        width: "12%",
        displayType: "quantity",
    },
    {
        headerName: "Return Qty",
        mappedValue: "returnCaseQty,returnPieceQty,returnOtherQty",
        width: "14%",
        displayType: "quantity",
        hasEditPenSvg: isPending ? true : false,
        redirectFunction: isPending ? handleRedirect : undefined
    },
    {
        headerName: "Return Value",
        mappedValue: "netAmount",
        width: "10%",
        displayType: "amount"
    },
];

const subTableMetaData = {
    mappedValue: "batchIdDetails",
    columns: [
        {
            headerName: "Batch Id",
            mappedValue: "batchId",
            width: "25%",
        },
        {
            headerName: "MRP",
            mappedValue: "mrp",
            width: "20%",
        },
        {
            headerName: "Returned Qty",
            mappedValue: "caseQty,pieceQty,otherQty",
            displayType: "quantity",
            width: "25%",
        },
        {
            headerName: "Reason",
            mappedValue: "reason",
            width: "30%",
        }
    ]
}

const outletCordianMetaData = [
    {
        keys: "outletCode",
        label: "Outlet Code",
        index: 1,
    },
    {
        keys: "outletName",
        label: "Outlet Name",
        index: 2,
    },
    {
        keys: "accountType",
        label: "Account Type",
        index: 3,
    },
    {
        keys: "loadoutNumber",
        label: "Loadout No.",
        index: 4,
    },
    {
        keys: "deliveryMode",
        label: "deliveryMode",
        index: 5,
    },
    {
        keys: "vehicleNumber",
        label: "Vehicle Number",
        index: 6,
        valueType: "number",
    },
];

const invoiceCordianMetaData = [
    {
        keys: "invoiceNumber",
        label: "Invoice No.",
        index: 1,
    },
    {
        keys: "orderNumber",
        label: "Order No.",
        index: 2,
    },
    {
        keys: "returnDate",
        label: "Return Date",
        index: 3,
    },
    {
        keys: "totalProducts",
        label: "Total Products",
        index: 4,
    },
    {
        keys: "invoiceValue",
        label: "Invoice Value",
        index: 5,
    },
    {
        keys: "returnValue",
        label: "Return Value",
        index: 6,
        valueType: "number",
    },
];
const getSalesReturnDetailsColumnMetaData = () => ({
    columns: [
        {
            headerName: "SKU Code",
            mappedValue: "batchCode",
            width: 50,
            headerAlign: "left",
            align: "left",
        },
        {
            headerName: "SKU Name",
            mappedValue: "skuName",
            width: 120,
            headerAlign: "left",
            align: "left",
        },
        {
            headerName: "Pack Type",
            mappedValue: "packType",
            width: 50,
            headerAlign: "left",
            align: "left",
        },
        {
            headerName: "Batch Ids",
            mappedValue: "batchIdCount",
            width: 55,
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "PTR",
            mappedValue: "price",
            width: 40,
            headerAlign: "left",
            align: "left",
            amountDisplay: true,
        },
        {
            headerName: "Sold Qty",
            mappedValue: "soldCaseQty,soldPieceQty,soldOtherQty",
            width: 65,
            headerAlign: "left",
            align: "left",
            qtyDisplay: true
        },
        {
            headerName: "Return Qty",
            mappedValue: "returnCaseQty,returnPieceQty,returnOtherQty",
            width: 75,
            headerAlign: "left",
            align: "left",
            qtyDisplay: true,
            hasEditPenSvg: true,
            isEditable: true
        },
        {
            headerName: "Return Value",
            mappedValue: "netAmount",
            amountDisplay: true,
            width: 60,
            headerAlign: "left",
            align: "left",
        },
    ],
    Actions: [],
});


export default ViewSaleReturnDetails;